import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import moment from 'moment-timezone';
import { formatCampaignName, hasSpecialCharacters } from '../../../../core/utils/campaignNaming';
import { getEffectiveCpm } from '../../../../core/utils/campaigns';
import { formatAdProviderIds } from '../../../../core/utils/formatAdProviderIds';
import { deriveStatus } from '../shared/StatusTag';

/**
 * Matches the dashboard's pacing column logic (CampaignsTable.js).
 * Handles both the new paceAdditionalInfo path (when pacingData.pacing exists)
 * and the legacy byDate calculation path.
 * When switchPacingYesterday is true: uses yesterday's values.
 * When false: uses today's values.
 * Finished/Attribution campaigns always use actual delivery numbers.
 */
function calculatePacingAndRemaining(campaignOrder, pacingData, switchPacingYesterday) {
  const { aggregateData } = campaignOrder;
  const overallData = aggregateData?.overallData || {};
  const paceAdditionalInfo = pacingData?.pacing;

  const status = deriveStatus(
    campaignOrder.startDate
      ? moment(campaignOrder.startDate).seconds(0).milliseconds(0).toISOString()
      : null,
    campaignOrder.endDate
      ? moment(campaignOrder.endDate).seconds(0).milliseconds(0).toISOString()
      : null,
    campaignOrder.attributionWindow || null,
    campaignOrder.status || null
  );

  // New pacing path — uses direct pacingData values (matches paceAdditionalInfo branch in CampaignsTable.js)
  if (paceAdditionalInfo) {
    let pace = pacingData?.pace || 0;
    let impressionsleft = pacingData?.impressionsLeft || 0;

    if (switchPacingYesterday) {
      pace = pacingData?.paceYesterday || 0;
      impressionsleft = pacingData?.impressionsLeftYesterday || 0;
    }

    // Override for finished campaigns
    if (status === 'FINISHED') {
      const impressionsSpent = overallData.aggImpressions || 0;
      pace = pacingData?.pace || 0;
      impressionsleft = (pacingData?.impressionGoal || 0) - impressionsSpent;
    }

    return { pace, impressionsleft };
  }

  // Legacy path — calculate from byDate (matches else branch in CampaignsTable.js)
  const { cpm, budget } = campaignOrder;
  const byDate = aggregateData?.byDate;

  let pace = switchPacingYesterday
    ? (pacingData?.paceYesterday ?? pacingData?.pace ?? 0)
    : (pacingData?.pace ?? 0);
  let impressionsleft = pacingData?.impressionsLeft || 0;

  if (!budget || !cpm) {
    return { pace, impressionsleft };
  }

  const impressionGoal = budget !== 1 && cpm ? (budget / cpm) * 1000 : 0;

  if (!impressionGoal) {
    return { pace, impressionsleft };
  }

  if (status === 'FINISHED' || status === 'ATTRIBUTION') {
    // Campaign past end date — use actual delivery numbers
    const impressionsSpent = overallData.aggImpressions || 0;
    pace = (impressionsSpent / impressionGoal) * 100;
    impressionsleft = impressionGoal - impressionsSpent;
  } else if (byDate && byDate.length > 0) {
    // Live campaign — calculate remaining from byDate
    const latestDate = byDate.reduce((latest, curr) => {
      const { xAxis } = curr;
      if (!latest) {
        return moment(xAxis, 'YYYY-MM-DD').format('YYYY-MM-DD');
      }
      if (moment(xAxis, 'YYYY-MM-DD').isAfter(latest, 'day')) {
        return moment(xAxis, 'YYYY-MM-DD').format('YYYY-MM-DD');
      }
      return latest;
    }, moment(campaignOrder.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));

    const daysToExclude = switchPacingYesterday ? 2 : 1;
    const impressionsSpent = byDate.reduce((acc, curr) => {
      const { xAxis, impressions } = curr;
      const cutoffDate = moment(latestDate, 'YYYY-MM-DD').subtract(daysToExclude, 'days');
      if (moment(xAxis, 'YYYY-MM-DD').isSameOrBefore(cutoffDate, 'day')) {
        acc += impressions;
      }
      return acc;
    }, 0);

    impressionsleft = impressionGoal - impressionsSpent;
  }

  return { pace, impressionsleft };
}

export default function exportCampaignTableData(dataExportCampaignTable, switchPacingYesterday, timezone = 'Etc/GMT-8') {
  const zip = new JSZip();
  const folder = zip.folder('Campaigns-Table-Data');

  const reportData = dataExportCampaignTable.campaignOrdersConnection.edges.map((edge) => {
    const campaignOrder = edge.node;
    const overallData =
      campaignOrder && campaignOrder.aggregateData ? campaignOrder.aggregateData.overallData : {};
    const pacingData = campaignOrder.pacingData || {};
    const cpm = getEffectiveCpm(campaignOrder);
    const budgetSpent = cpm * (overallData.aggImpressions / 1000);
    const { pace, impressionsleft } = calculatePacingAndRemaining(
      campaignOrder,
      pacingData,
      switchPacingYesterday
    );

    return {
      'Campaign Order ID': campaignOrder.id,
      'Campaign Title': !hasSpecialCharacters(campaignOrder.name)
        ? formatCampaignName(campaignOrder, timezone)
        : campaignOrder.name,
      'Org Name': campaignOrder.orgs.map((org) => org.name).join(', '),
      'Start Date': campaignOrder.startDate ? moment(campaignOrder.startDate).tz(timezone).format('MM/DD/YYYY') : '',
      'End Date': campaignOrder.endDate ? moment(campaignOrder.endDate).tz(timezone).format('MM/DD/YYYY') : '',
      'No. of Days into flight': Math.max(
        0,
        Math.ceil(
          (new Date(campaignOrder.endDate).getTime() -
            new Date(campaignOrder.startDate).getTime()) /
            (1000 * 60 * 60 * 24) // convert milliseconds to days
        )
      ),
      'Days Remaining': Math.max(
        0,
        Math.ceil(
          (new Date(campaignOrder.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24) // convert milliseconds to days
        )
      ),
      Budget: campaignOrder.budget,
      CPM: cpm,
      'Expected Impressions': pacingData.expectedImpressions || 0,
      'Used Impressions': overallData.aggImpressions || 0,
      'Remaining Impressions': impressionsleft,
      Pacing: pace,
      'Walk Ins Data': overallData.aggVWI || 0,
      Clicks: overallData.aggClicks || 0,
      CTR: pacingData.ctr || 0,
      Orders:
        overallData && overallData.aggEvents && overallData.aggEvents.transactionsTotalCount
          ? overallData.aggEvents.transactionsTotalCount
          : 0,
      Spend: isNaN(budgetSpent) ? 0 : budgetSpent.toFixed(2),
      Revenue:
        overallData && overallData.aggEvents && overallData.aggEvents.transactionTotalRevenue
          ? overallData.aggEvents.transactionTotalRevenue.toFixed(2)
          : 0,
      ROAS:
        overallData &&
        overallData.aggEvents &&
        overallData.aggEvents.transactionTotalRevenue &&
        budgetSpent !== 0
          ? (overallData.aggEvents.transactionTotalRevenue / budgetSpent).toFixed(2) * 100
          : 0,
      'Ad Provider IDs': formatAdProviderIds(campaignOrder.campaigns),
    };
  });

  const date = new Date();
  const dateString = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

  folder.file(`Campaigns-Table-Data-${dateString}.csv`, Papa.unparse(reportData));

  zip.generateAsync({ type: 'blob' }).then(
    (blob) => {
      saveAs(blob, `Campaigns Table Data ${dateString}`);
      return true;
    },
    function (e) {
      console.log('error', e);
      return false;
    }
  );
}
